import { Component, define } from '@theme/component';

/**
 * The options on the product page: dropdowns, or with a single option the
 * package cards (a fieldset of radios).
 *
 * The variants are in a JSON script the section renders; choosing a value
 * finds the variant with that combination and updates everything that
 * depends on it — the hidden variant id the form posts, the price, the
 * button, the photograph and the `?variant=` in the address bar, so a shared
 * link opens on the same choice.
 *
 * A product with a single variant renders no dropdowns, and this does
 * nothing.
 */
class ProductForm extends Component {
  static requiredRefs = ['variants', 'variantId', 'button'];

  /** @type {Array<{id: number, options: string[], available: boolean, price: string, compareAtPrice: string | null, mediaId: number | null}>} */
  #variants = [];

  connected() {
    try {
      this.#variants = JSON.parse(this.refs.variants.textContent || '[]');
    } catch {
      this.#variants = [];
    }

    for (const select of this.#selects) {
      this.on(select, 'change', this.#onChange);
    }
  }

  /** The photo of the checked choice of the selected package, if it has one
      — the grinder colour shows that grinder with the machine. */
  get #choiceMediaId() {
    const input = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-package-choice]:not([disabled]) input:checked[data-media-id]')
    );
    return input ? Number(input.dataset.mediaId) : null;
  }

  /** The dropdowns, or the fieldsets of package cards. */
  get #selects() {
    return /** @type {Array<HTMLSelectElement | HTMLFieldSetElement>} */ ([this.refs.option ?? []].flat());
  }

  /** @param {HTMLSelectElement | HTMLFieldSetElement} control */
  #valueOf(control) {
    if (control instanceof HTMLSelectElement) return control.value;
    // Only the card radios: a package's own choice (the grinder colour) sits
    // in the same fieldset and is checked too.
    return /** @type {HTMLInputElement | null} */ (control.querySelector('.package > input:checked'))?.value ?? '';
  }

  #onChange = () => {
    const chosen = this.#selects.map((control) => this.#valueOf(control));

    // A package's choice is submitted only with that package.
    for (const choice of this.querySelectorAll('[data-package-choice]')) {
      choice.toggleAttribute('disabled', !chosen.includes(/** @type {HTMLElement} */ (choice).dataset.packageChoice ?? ''));
    }

    const variant = this.#variants.find((candidate) =>
      candidate.options.every((value, index) => value === chosen[index])
    );

    this.#render(variant);
  };

  /** @param {(typeof this.#variants)[number] | undefined} variant */
  #render(variant) {
    const { button, buttonLabel, variantId } = this.refs;
    const label = /** @type {HTMLElement} */ (buttonLabel);

    if (!variant) {
      button.toggleAttribute('disabled', true);
      if (label) label.textContent = this.dataset.unavailableText ?? '';
      return;
    }

    /** @type {HTMLInputElement} */ (variantId).value = String(variant.id);
    button.toggleAttribute('disabled', !variant.available);

    if (label) {
      // The available label is whatever Liquid rendered first; keep it.
      label.dataset.availableText ??= label.textContent ?? '';
      label.textContent = variant.available ? label.dataset.availableText : this.dataset.soldOutText ?? '';
    }

    this.#renderPrice(variant);
    this.#showChoicePhoto();

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', url);
  }

  /** @param {(typeof this.#variants)[number]} variant */
  #renderPrice(variant) {
    // The buy column and the sticky bar each show the price.
    const slots = this.closest('[data-product-root]')?.querySelectorAll('[data-product-price] .price') ?? [];

    for (const slot of slots) {
      slot.replaceChildren(...this.#priceParts(variant));
    }
  }

  /** @param {(typeof this.#variants)[number]} variant */
  #priceParts(variant) {
    const current = document.createElement('span');
    current.textContent = variant.price;
    const parts = [current];

    if (variant.compareAtPrice) {
      const was = document.createElement('s');
      was.textContent = variant.compareAtPrice;
      parts.push(was);
    }

    return parts;
  }

  /** The photo of the picked choice takes the first place in the gallery,
      in place, without scrolling; the other choices' photos are hidden. With
      no choice picked — "Machine only" — they are all hidden. */
  #showChoicePhoto() {
    const section = this.closest('[data-product-root]');
    const photos = /** @type {HTMLElement[]} */ ([...(section?.querySelectorAll('[data-choice-photo]') ?? [])]);
    if (photos.length === 0) return;

    // Showing a photo in front makes the browser scroll on to keep the old
    // first photo in view; on the first photo, stay on the first photo.
    const track = /** @type {HTMLElement | null} */ (photos[0].parentElement);
    const atStart = (track?.scrollLeft ?? 0) < 5;

    const active = this.#choiceMediaId;
    for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('[data-package-choice]:not([disabled]) [data-choice-photo-input]')
    )) {
      if (active) input.value = String(active);
    }

    for (const photo of photos) {
      photo.hidden = Number(photo.dataset.mediaId) !== active;
      if (!photo.hidden) photo.parentElement?.prepend(photo);
    }

    if (atStart) track?.scrollTo({ left: 0, behavior: 'instant' });

    /** @type {any} */ (section?.querySelector('slider-component'))?.refresh?.();
  }
}

define('product-form', ProductForm);
